import { useMemo } from 'react';
import type Highcharts from 'highcharts';
import type { DirectShearReading } from '../../../../types/directShearReading';
import type { AnalysisData } from '../analysisTypes';
import type { MarkerRow } from '../chartConfig';
import { baseChartOptions, CHART_COLORS, buildColorZones, buildMarkerSeries } from '../chartConfig';

interface FrictionAngleChartProps {
  readings: DirectShearReading[];
  analysis: AnalysisData;
  peakRow: MarkerRow;
  residualRow: MarkerRow;
}

export function useFrictionAngleChartOptions({ readings, analysis, peakRow, residualRow }: FrictionAngleChartProps): Highcharts.Options {
  return useMemo((): Highcharts.Options => {
    const data = readings
      .filter((r) => r.HorizontalDisplacementCumulative != null && r.CorrectedFrictionAngle != null)
      .map((r) => [r.HorizontalDisplacementCumulative!, r.CorrectedFrictionAngle!]);

    const zones = buildColorZones(analysis);
    const markers = buildMarkerSeries(peakRow, residualRow, 'CorrectedFrictionAngle');

    return {
      ...baseChartOptions,
      title: { text: 'Corrected Friction Angle vs. Horizontal Displacement' },
      yAxis: {
        ...(baseChartOptions.yAxis as Highcharts.YAxisOptions),
        title: { text: 'Friction Angle (°)' },
      },
      series: [
        {
          type: 'spline',
          name: 'Corrected Friction Angle',
          data,
          color: CHART_COLORS.green,
          zoneAxis: zones ? 'x' : undefined,
          zones,
        },
        ...markers,
      ],
    };
  }, [readings, analysis, peakRow, residualRow]);
}
